import { DictationError, type PcmCaptureHandlers, type StartPcmCapture } from '../types';

import { permissionManager, preferenceStore, rememberedDeviceId, streamAcquirer } from './microphones';

/** About 100 ms of audio per message: low socket chatter, no perceptible lag. */
const CHUNK_SECONDS = 0.1;

/**
 * Runs on the audio rendering thread. Converts Float32 frames to Int16 PCM,
 * batches them into ~100 ms chunks, and reports the chunk's RMS level.
 * `sampleRate` is a global in the AudioWorklet scope.
 */
const WORKLET_SOURCE = `
class JotPcm16Capture extends AudioWorkletProcessor {
  constructor() {
    super();
    this.chunkSize = Math.max(128, Math.round(sampleRate * ${CHUNK_SECONDS}));
    this.buffer = new Int16Array(this.chunkSize);
    this.offset = 0;
    this.sumSquares = 0;
  }
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (!channel) return true;
    for (let i = 0; i < channel.length; i++) {
      const s = Math.max(-1, Math.min(1, channel[i]));
      this.sumSquares += s * s;
      this.buffer[this.offset++] = s < 0 ? s * 0x8000 : s * 0x7fff;
      if (this.offset === this.chunkSize) this.flush();
    }
    return true;
  }
  flush() {
    const level = Math.sqrt(this.sumSquares / this.offset);
    const pcm = this.buffer.buffer.slice(0, this.offset * 2);
    this.port.postMessage({ pcm, level }, [pcm]);
    this.offset = 0;
    this.sumSquares = 0;
  }
}
registerProcessor('jot-pcm16-capture', JotPcm16Capture);
`;

export function browserSupportsDictation(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof window.AudioContext === 'function'
  );
}

/** The recorder's classification, so both surfaces explain a failure the same way. */
function toCaptureError(error: unknown): DictationError {
  const result = permissionManager.classifyError(error);
  const code =
    result.error === 'not_allowed'
      ? 'permission_denied'
      : result.error === 'not_found'
        ? 'no_microphone'
        : 'unsupported';
  return new DictationError(code, result.message ?? 'The microphone could not be started.');
}

async function attachWorklet(
  context: AudioContext,
  source: MediaStreamAudioSourceNode,
  handlers: PcmCaptureHandlers,
): Promise<() => void> {
  const url = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
  try {
    await context.audioWorklet.addModule(url);
  } finally {
    URL.revokeObjectURL(url);
  }
  const node = new AudioWorkletNode(context, 'jot-pcm16-capture', {
    numberOfInputs: 1,
    numberOfOutputs: 0,
    channelCount: 1,
  });
  node.port.onmessage = (event: MessageEvent<{ pcm: ArrayBuffer; level: number }>) => {
    handlers.onLevel(event.data.level);
    handlers.onChunk(event.data.pcm);
  };
  source.connect(node);
  return () => {
    node.port.onmessage = null;
    source.disconnect();
    node.disconnect();
  };
}

/**
 * Fallback for engines without AudioWorklet. The API is deprecated but still
 * shipped by every browser; it is only reached when the worklet is missing.
 */
/* eslint-disable @typescript-eslint/no-deprecated */
function attachScriptProcessor(
  context: AudioContext,
  source: MediaStreamAudioSourceNode,
  handlers: PcmCaptureHandlers,
): () => void {
  // bufferSize must be a power of two between 256 and 16384.
  const exponent = Math.round(Math.log2(context.sampleRate * CHUNK_SECONDS));
  const bufferSize = Math.min(16384, Math.max(256, 2 ** exponent));
  const node = context.createScriptProcessor(bufferSize, 1, 1);
  node.onaudioprocess = event => {
    const input = event.inputBuffer.getChannelData(0);
    const pcm = new Int16Array(input.length);
    let sumSquares = 0;
    for (let i = 0; i < input.length; i++) {
      const s = Math.max(-1, Math.min(1, input[i]));
      sumSquares += s * s;
      pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    handlers.onLevel(Math.sqrt(sumSquares / input.length));
    handlers.onChunk(pcm.buffer);
  };
  // The processor only runs when it reaches the destination; a muted gain keeps
  // the microphone out of the speakers.
  const sink = context.createGain();
  sink.gain.value = 0;
  source.connect(node);
  node.connect(sink);
  sink.connect(context.destination);
  return () => {
    node.onaudioprocess = null;
    source.disconnect();
    node.disconnect();
    sink.disconnect();
  };
}
/* eslint-enable @typescript-eslint/no-deprecated */

/**
 * Opens the microphone and streams mono PCM16 chunks. The context keeps the
 * hardware sample rate (Safari refuses mismatched media-stream sources); the
 * engine is told the real rate instead of resampling here.
 */
export const startPcmCapture: StartPcmCapture = async (handlers, options = {}) => {
  if (!browserSupportsDictation()) {
    throw new DictationError('unsupported', 'Dictation is not supported in this browser.');
  }
  let stream: MediaStream;
  try {
    // Same path as the encounter recorder: exact device, then the default if it
    // is gone or busy. The device actually opened becomes the remembered one.
    const requested = options.deviceId ?? (await rememberedDeviceId());
    const acquired = await streamAcquirer.acquire(requested);
    preferenceStore.save(acquired.device);
    stream = acquired.stream;
  } catch (error) {
    throw toCaptureError(error);
  }
  const context = new AudioContext();
  const release = () => {
    stream.getTracks().forEach(track => track.stop());
    void context.close();
  };
  try {
    await context.resume();
    const source = context.createMediaStreamSource(stream);
    const detach = context.audioWorklet
      ? await attachWorklet(context, source, handlers)
      : attachScriptProcessor(context, source, handlers);
    return {
      sampleRate: context.sampleRate,
      stop: () => {
        detach();
        release();
      },
    };
  } catch (error) {
    release();
    throw error instanceof DictationError ? error : toCaptureError(error);
  }
};
